import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, ShieldCheck, ImageIcon, Layers, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { regionToFlag } from "@/lib/regions";
import { motion } from "framer-motion";

/** Hover dwell before a grid video starts playing. See AssetCard. */
const HOVER_PLAY_DELAY_MS = 500;

/**
 * Asset library: every generated image, in one grid.
 *
 * Previously this showed only approved (is_master) visuals, which hid ~91% of
 * what had been generated and made images appear to vanish after generation.
 * Approval is now a marker rather than a gate — useful for "this is what we
 * shipped", not a precondition for the image existing here.
 *
 * Clicking an image opens the editor and nothing else. The library used to
 * carry its own lightbox, action menu and download button; downloads now
 * happen only in the editor, which is also where AI-provenance marking runs,
 * so there is one path to a file rather than several to keep in sync.
 */

interface AssetItem {
  id: string;
  kind: "image" | "video";
  /** Video source; absent for stills. */
  videoUrl?: string;
  /** The visual this belongs to — videos open their parent's editor. */
  visualId: string;
  imageUrl: string;
  storagePath: string;
  createdAt: string;
  isMaster: boolean;
  source: "brief" | "free";
  productId: string | null;
  productName: string;
  originName: string;
  deliverableCount: number;
}

/**
 * Grid card. Stills render as an image; videos show their own first frame and
 * begin playing after a hover delay.
 *
 * 500 ms is chosen deliberately. Hover-intent guidance for revealing content
 * sits around 150-300 ms, but that assumes a cheap reveal like a tooltip.
 * Here a false trigger costs a video download, and with a couple of hundred
 * cards a cursor crossing the grid would otherwise start a burst of them. Long
 * enough to mean "the pointer stopped here", short enough not to feel broken.
 *
 * The clip is muted and loops: audio on hover is the widely disliked part of
 * this pattern, and Veo bakes in an audio track we mute everywhere else too.
 */
function AssetCard({
  asset, onOpen, children,
}: {
  asset: AssetItem;
  onOpen: () => void;
  children: React.ReactNode;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const [playing, setPlaying] = useState(false);

  const startHover = () => {
    if (asset.kind !== "video") return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      videoRef.current?.play().then(() => setPlaying(true)).catch(() => {
        /* autoplay refused — leave the first frame showing */
      });
    }, HOVER_PLAY_DELAY_MS);
  };

  const endHover = () => {
    clearTimeout(timerRef.current);
    const el = videoRef.current;
    if (el) {
      el.pause();
      el.currentTime = 0; // back to the first frame
    }
    setPlaying(false);
  };

  // Never leave a timer running against an unmounted card.
  useEffect(() => () => clearTimeout(timerRef.current), []);

  return (
    <button
      onClick={onOpen}
      onMouseEnter={startHover}
      onMouseLeave={endHover}
      onFocus={startHover}
      onBlur={endHover}
      className="group relative rounded-xl overflow-hidden border border-border bg-muted/30 text-left hover:ring-2 hover:ring-primary/40 transition-all"
      title={`${asset.productName} · ${asset.originName}`}
    >
      {asset.kind === "video" ? (
        <video
          ref={videoRef}
          src={asset.videoUrl}
          // metadata alone is enough to paint the first frame, without
          // pulling the whole clip for every card in the grid.
          preload="metadata"
          muted
          loop
          playsInline
          className="w-full aspect-square object-cover"
        />
      ) : (
        <img
          src={asset.imageUrl}
          alt={asset.productName}
          loading="lazy"
          className="w-full aspect-square object-cover"
        />
      )}

      {asset.kind === "video" && (
        <span
          className={cn(
            "absolute inset-0 flex items-center justify-center transition-opacity",
            playing ? "opacity-0" : "opacity-100",
          )}
        >
          <span className="w-9 h-9 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center">
            <Play className="w-4 h-4 translate-x-[1px]" />
          </span>
        </span>
      )}

      {children}
    </button>
  );
}

export default function AssetLibrary() {
  const { workspace } = useWorkspace();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [assets, setAssets] = useState<AssetItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [approvedOnly, setApprovedOnly] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<"all" | "brief" | "free">("all");
  const [productFilter, setProductFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<"all" | "image" | "video">("all");

  useEffect(() => {
    if (workspace) void loadAssets();
  }, [workspace?.id]);

  const loadAssets = async () => {
    if (!workspace) return;
    setLoading(true);

    const { data: visuals } = await supabase
      .from("visuals")
      .select("id, storage_path, created_at, is_master, source, product_id, brief_id, session_id, videos, root_render_id")
      .eq("workspace_id", workspace.id)
      .eq("status", "complete")
      .not("storage_path", "is", null)
      .order("created_at", { ascending: false });

    if (!visuals || visuals.length === 0) {
      setAssets([]);
      setLoading(false);
      return;
    }

    const rows = visuals as any[];

    // Resolve display names in bulk rather than per row.
    const productIds = [...new Set(rows.map((r) => r.product_id).filter(Boolean))];
    const briefIds = [...new Set(rows.map((r) => r.brief_id).filter(Boolean))];
    const sessionIds = [...new Set(rows.map((r) => r.session_id).filter(Boolean))];
    const visualIds = rows.map((r) => r.id);

    const [productRes, briefRes, sessionRes, delRes] = await Promise.all([
      productIds.length
        ? supabase.from("products").select("id, name").in("id", productIds)
        : Promise.resolve({ data: [] }),
      briefIds.length
        // wizard_configs carries the region; briefs only names the config it
        // used, so the flag needs the join.
        ? supabase.from("briefs")
            .select("id, name, wizard_configs(region_key)")
            .in("id", briefIds)
        : Promise.resolve({ data: [] }),
      sessionIds.length
        ? supabase.from("studio_sessions" as any).select("id, name").in("id", sessionIds)
        : Promise.resolve({ data: [] }),
      supabase.from("deliverables").select("visual_id, videos").in("visual_id", visualIds),
    ]);

    const productNames = new Map(((productRes.data as any[]) || []).map((p) => [p.id, p.name]));
    // Name prefixed with the market's flag: a Norwegian scene and a Swedish
    // one can carry the same name and produce very different images.
    const briefNames = new Map(((briefRes.data as any[]) || []).map((b) => {
      const flag = regionToFlag((b.wizard_configs as any)?.region_key);
      return [b.id, `${flag} ${b.name}`];
    }));
    const sessionNames = new Map(((sessionRes.data as any[]) || []).map((s) => [s.id, s.name]));

    const delCounts = new Map<string, number>();
    for (const d of ((delRes.data as any[]) || [])) {
      delCounts.set(d.visual_id, (delCounts.get(d.visual_id) || 0) + 1);
    }

    const videoItems: AssetItem[] = [];
    const pushVideos = (list: any, parent: any, originSuffix: string) => {
      for (const v of (Array.isArray(list) ? list : [])) {
        if (!v?.path) continue;
        const { data } = supabase.storage.from("renders").getPublicUrl(v.path);
        const { data: posterData } = supabase.storage.from("renders").getPublicUrl(parent.storage_path);
        const isFree = parent.source === "free";
        videoItems.push({
          id: `video-${v.id}`,
          kind: "video",
          videoUrl: data.publicUrl,
          visualId: parent.id,
          // The parent still, used when opening the editor. Playback uses
          // videoUrl and shows the clip's own first frame.
          imageUrl: posterData.publicUrl,
          storagePath: v.path,
          createdAt: v.createdAt || parent.created_at,
          isMaster: !!parent.is_master,
          source: isFree ? "free" : "brief",
          productId: parent.product_id || null,
          productName: parent.product_id
            ? (productNames.get(parent.product_id) || "Unknown product")
            : "No product",
          originName: (isFree
            ? (sessionNames.get(parent.session_id) || "Studio session")
            : (briefNames.get(parent.brief_id) || "Scene")) + originSuffix,
          deliverableCount: 0,
        });
      }
    };

    for (const r of rows) pushVideos(r.videos, r, "");
    const visualById = new Map(rows.map((r) => [r.id, r]));
    for (const d of ((delRes.data as any[]) || [])) {
      const parent = visualById.get(d.visual_id);
      if (parent) pushVideos(d.videos, parent, " · format");
    }

    // One card per lineage. An edit supersedes the version it came from, so a
    // chain of edits should read as one asset at its newest state rather than
    // as a pile of near-identical images. `rows` is already newest-first, so
    // the first row seen for a lineage is the one to keep.
    //
    // Videos are deliberately not collapsed: a clip is its own asset, not a
    // newer state of the still it was generated from.
    const latestOfLineage = new Map<string, any>();
    for (const r of rows) {
      const lineage = r.root_render_id || r.id;
      if (!latestOfLineage.has(lineage)) latestOfLineage.set(lineage, r);
    }

    const imageItems: AssetItem[] = [...latestOfLineage.values()].map((r) => {
      const { data: urlData } = supabase.storage.from("renders").getPublicUrl(r.storage_path);
      const isFree = r.source === "free";
      return {
        id: r.id,
        kind: "image" as const,
        visualId: r.id,
        imageUrl: urlData.publicUrl,
        storagePath: r.storage_path,
        createdAt: r.created_at,
        isMaster: !!r.is_master,
        source: isFree ? "free" : "brief",
        productId: r.product_id || null,
        productName: r.product_id ? (productNames.get(r.product_id) || "Unknown product") : "No product",
        originName: isFree
          ? (sessionNames.get(r.session_id) || "Studio session")
          : (briefNames.get(r.brief_id) || "Scene"),
        deliverableCount: delCounts.get(r.id) || 0,
      };
    });

    // Interleave by recency so videos aren't stranded at the end.
    setAssets([...imageItems, ...videoItems].sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
    setLoading(false);
  };

  const products = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of assets) if (a.productId) map.set(a.productId, a.productName);
    return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [assets]);

  const filtered = useMemo(() => {
    let result = assets;
    if (approvedOnly) result = result.filter((a) => a.isMaster);
    if (sourceFilter !== "all") result = result.filter((a) => a.source === sourceFilter);
    if (productFilter !== "all") result = result.filter((a) => a.productId === productFilter);
    if (typeFilter !== "all") result = result.filter((a) => a.kind === typeFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (a) => a.productName.toLowerCase().includes(q) || a.originName.toLowerCase().includes(q),
      );
    }
    return result;
  }, [assets, approvedOnly, sourceFilter, productFilter, typeFilter, searchQuery]);

  const approvedCount = assets.filter((a) => a.isMaster).length;

  /**
   * One destination per asset: the editor. Downloads live there too.
   *
   * A video opens its parent visual's editor — that page already hosts the
   * video player and version list, so the video is reachable there rather than
   * needing an editor of its own.
   */
  const openInEditor = (asset: AssetItem) => {
    // Identity in the URL so the editor survives a refresh and Back has a
    // destination that does not depend on router state.
    navigate(`/app/edit-visual?v=${asset.visualId}&from=${encodeURIComponent("/app/asset-library")}`, {
      state: {
        imageUrl: asset.imageUrl,
        allVersionUrls: [asset.imageUrl],
        renderId: asset.visualId,
        parentRenderId: asset.visualId,
        briefId: null,
        returnTo: "/app/asset-library",
      },
    });
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-7xl mx-auto">
      <div className="mb-5">
        <h1 className="font-display text-2xl font-bold">Assets</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {filtered.length} of {assets.length} image{assets.length !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Filters — binary state as chips, growing lists as dropdowns */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="flex items-center gap-1 rounded-lg border border-border p-0.5">
          {([
            { key: false, label: "All", count: assets.length },
            { key: true, label: "Approved", count: approvedCount },
          ] as const).map((opt) => (
            <button
              key={String(opt.key)}
              onClick={() => setApprovedOnly(opt.key)}
              className={cn(
                "px-2.5 py-1 rounded-md text-xs font-medium transition-colors",
                approvedOnly === opt.key ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {opt.label}
              <span className="ml-1.5 text-[10px] text-muted-foreground tabular-nums">{opt.count}</span>
            </button>
          ))}
        </div>

        <Select value={productFilter} onValueChange={setProductFilter}>
          <SelectTrigger className="h-8 w-auto min-w-[9rem] text-xs">
            <SelectValue placeholder="Product" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All products</SelectItem>
            {products.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={sourceFilter} onValueChange={(v) => setSourceFilter(v as any)}>
          <SelectTrigger className="h-8 w-auto min-w-[8rem] text-xs">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="brief">From a scene</SelectItem>
            <SelectItem value="free">Free prompt</SelectItem>
          </SelectContent>
        </Select>

        <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as any)}>
          <SelectTrigger className="h-8 w-auto min-w-[7rem] text-xs">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Images & video</SelectItem>
            <SelectItem value="image">Images</SelectItem>
            <SelectItem value="video">Video</SelectItem>
          </SelectContent>
        </Select>

        <div className="relative flex-1 min-w-[12rem] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="Search…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-8 text-xs"
          />
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <ImageIcon className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">
            {assets.length === 0
              ? "No images yet. Generate some in Studio."
              : "No images match these filters."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {filtered.map((asset) => (
            <AssetCard key={asset.id} asset={asset} onOpen={() => openInEditor(asset)}>

              {asset.isMaster && (
                <span className="absolute top-2 left-2 w-6 h-6 rounded-full bg-background/85 backdrop-blur-sm flex items-center justify-center">
                  <ShieldCheck className="w-3.5 h-3.5 text-primary" />
                </span>
              )}

              {asset.deliverableCount > 0 && (
                <span className="absolute top-2 right-2 inline-flex items-center gap-1 h-6 px-1.5 rounded-full bg-background/85 backdrop-blur-sm text-[10px] font-medium">
                  <Layers className="w-3 h-3" />
                  {asset.deliverableCount}
                </span>
              )}

              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2.5 py-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="text-[11px] font-medium text-white truncate">{asset.productName}</p>
                <p className="text-[10px] text-white/70 truncate">{asset.originName}</p>
              </div>
            </AssetCard>
          ))}
        </div>
      )}
    </motion.div>
  );
}
